import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Modal } from 'react-bootstrap'
import { KTSVG } from '_metronic/helpers'
import { toast } from 'react-toastify'
import JobClosingLineRow, { FormLine } from './components/JobClosingLineRow'
import { JobClosingDocument, JobClosingDocumentInput } from './types'
import { useJobClosingContext } from './Context'

interface Props {
  isOpen: boolean
  editingDocument?: JobClosingDocument | null
  defaultCompanyName?: string
  onClose: () => void
  onSuccess?: () => void
}

let clientLineCounter = 0

function nextClientLineId() {
  clientLineCounter += 1
  return `client-line-${clientLineCounter}`
}

function createPileLine(sequence: number): FormLine {
  return {
    clientId: nextClientLineId(),
    sequence,
    lineType: 'pile',
    inventoryCode: '',
    quantity: 1,
    length: null,
  }
}

function createExpenseLine(sequence: number): FormLine {
  return {
    clientId: nextClientLineId(),
    sequence,
    lineType: 'expense',
    description: '',
    amount: 1,
  }
}

function documentToFormLines(document?: JobClosingDocument | null): FormLine[] {
  if (!document?.lines.length) return []
  return document.lines.map(line => {
    if (line.lineType === 'pile') {
      return {
        clientId: nextClientLineId(),
        sequence: line.sequence,
        lineType: 'pile' as const,
        inventoryCode: line.inventoryCode,
        quantity: line.quantity,
        length: line.length ?? null,
      }
    }
    return {
      clientId: nextClientLineId(),
      sequence: line.sequence,
      lineType: 'expense' as const,
      description: line.description,
      amount: line.amount ?? null,
    }
  })
}

function formLinesToInput(lines: FormLine[]): JobClosingDocumentInput['lines'] {
  return lines.map(line => {
    if (line.lineType === 'pile') {
      return {
        lineType: 'pile' as const,
        inventoryCode: line.inventoryCode ?? '',
        quantity: line.quantity ?? 1,
        length: line.length ?? null,
      }
    }
    return {
      lineType: 'expense' as const,
      description: line.description ?? '',
      amount: line.amount ?? 1,
    }
  })
}

const DEFAULT_HEAD = {
  subject: 'ขอให้ตรวจ และรับมอบงานตอกเสาเข็ม',
  addressee: 'ผู้ควบคุมงานตอกเสาเข็มคอนกรีตอัดแรง',
  companyName: '',
  remark: '',
}

const JobClosingForm: React.FC<Props> = ({
  isOpen,
  editingDocument,
  defaultCompanyName = '',
  onClose,
  onSuccess,
}) => {
  const modalsRoot = window.document.getElementById('root-modals') || window.document.body
  const { createDocument, updateDocument, issueDocument, isSubmitting } = useJobClosingContext()
  const isEdit = !!editingDocument

  const [subject, setSubject] = useState(DEFAULT_HEAD.subject)
  const [addressee, setAddressee] = useState(DEFAULT_HEAD.addressee)
  const [companyName, setCompanyName] = useState('')
  const [remark, setRemark] = useState('')
  const [lines, setLines] = useState<FormLine[]>([])

  useEffect(() => {
    if (!isOpen) return
    if (editingDocument) {
      setSubject(editingDocument.subject)
      setAddressee(editingDocument.addressee)
      setCompanyName(editingDocument.companyName)
      setRemark(editingDocument.remark ?? '')
      setLines(documentToFormLines(editingDocument))
    } else {
      setSubject(DEFAULT_HEAD.subject)
      setAddressee(DEFAULT_HEAD.addressee)
      setCompanyName(defaultCompanyName)
      setRemark('')
      setLines([])
    }
  }, [isOpen, editingDocument, defaultCompanyName])

  const buildInput = (): JobClosingDocumentInput => ({
    subject,
    addressee,
    companyName,
    remark: remark || undefined,
    lines: formLinesToInput(lines),
  })

  const validate = (): string | null => {
    if (!companyName.trim()) return 'กรุณากรอกบริษัท / หจก.'
    if (lines.length === 0) return 'กรุณาเพิ่มรายการอย่างน้อย 1 แถว'
    for (const line of lines) {
      if (line.lineType === 'pile') {
        if (!line.inventoryCode?.trim()) return 'กรุณากรอกชนิด/ขนาดเสาในทุกแถวประเภทเสา'
      } else if (!line.description?.trim()) {
        return 'กรุณากรอกรายละเอียดค่าใช้จ่าย'
      }
    }
    return null
  }

  const handleSave = async (issue: boolean) => {
    const error = validate()
    if (error) {
      toast.error(error)
      return
    }

    try {
      const input = buildInput()
      if (isEdit && editingDocument) {
        await updateDocument(editingDocument.id, input)
        if (issue) {
          await issueDocument(editingDocument.id)
        }
        toast.success(issue ? 'บันทึกและออกเอกสารสำเร็จ' : 'บันทึกร่างสำเร็จ')
      } else {
        await createDocument(input, issue)
        toast.success(issue ? 'สร้างและออกเอกสารสำเร็จ' : 'บันทึกร่างสำเร็จ')
      }
      onSuccess?.()
      onClose()
    } catch (err: any) {
      toast.error(err?.message ?? 'บันทึกไม่สำเร็จ')
    }
  }

  const addPileLine = () => {
    setLines(prev => [...prev, createPileLine(prev.length + 1)])
  }

  const addExpenseLine = () => {
    setLines(prev => [...prev, createExpenseLine(prev.length + 1)])
  }

  const updateLine = (clientId: string, updated: FormLine) => {
    setLines(prev => prev.map(l => (l.clientId === clientId ? updated : l)))
  }

  const removeLine = (clientId: string) => {
    setLines(prev =>
      prev.filter(l => l.clientId !== clientId).map((l, i) => ({ ...l, sequence: i + 1 }))
    )
  }

  return createPortal(
    <Modal
      id='kt_modal_job_closing_form'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered modal-dialog-scrollable mw-1000px'
      show={isOpen}
      onHide={() => !isSubmitting && onClose()}
      backdrop={isSubmitting ? 'static' : true}
      keyboard={!isSubmitting}>
      <div className='modal-content'>
        <div className='modal-header'>
          <h2>{isEdit ? 'แก้ไขใบปิดงาน' : 'สร้างใบปิดงาน'}</h2>
          {!isSubmitting && (
            <div className='btn btn-sm btn-icon btn-active-light-danger' onClick={onClose}>
              <KTSVG className='svg-icon-1' path='/media/icons/duotune/general/gen040.svg' />
            </div>
          )}
        </div>

        <div className='modal-body'>
          <div className='row g-4 mb-6'>
            <div className='col-md-6'>
              <label className='form-label required'>เรื่อง</label>
              <input
                type='text'
                className='form-control'
                value={subject}
                disabled={isSubmitting}
                onChange={e => setSubject(e.target.value)}
              />
            </div>
            <div className='col-md-6'>
              <label className='form-label required'>เรียน</label>
              <input
                type='text'
                className='form-control'
                value={addressee}
                disabled={isSubmitting}
                onChange={e => setAddressee(e.target.value)}
              />
            </div>
            <div className='col-md-6'>
              <label className='form-label required'>บริษัท / หจก.</label>
              <input
                type='text'
                className='form-control'
                value={companyName}
                disabled={isSubmitting}
                onChange={e => setCompanyName(e.target.value)}
              />
            </div>
            <div className='col-md-6'>
              <label className='form-label'>งวดที่</label>
              <input
                type='text'
                inputMode='numeric'
                pattern='[0-9]*'
                className='form-control'
                value={remark}
                disabled={isSubmitting}
                onChange={e => setRemark(e.target.value.replace(/\D/g, ''))}
              />
            </div>
          </div>

          <div className='d-flex flex-wrap gap-2 mb-4'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              disabled={isSubmitting}
              onClick={addPileLine}>
              <KTSVG path='/media/icons/duotune/general/gen041.svg' className='svg-icon-3 me-1' />
              เพิ่มเสา
            </button>
            <button
              type='button'
              className='btn btn-sm btn-light-warning'
              disabled={isSubmitting}
              onClick={addExpenseLine}>
              <KTSVG path='/media/icons/duotune/general/gen041.svg' className='svg-icon-3 me-1' />
              เพิ่มค่าใช้จ่าย
            </button>
          </div>

          <div className='table-responsive'>
            <table className='table table-row-bordered table-sm align-middle'>
              <thead className='bg-light'>
                <tr className='text-gray-700 fw-bold fs-7'>
                  <th style={{ width: 50 }}>ลำดับ</th>
                  <th style={{ width: 100 }}>ประเภท</th>
                  <th>รายละเอียด</th>
                  <th style={{ width: 100 }}>จำนวน</th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr>
                    <td colSpan={5} className='text-center text-gray-500 py-8'>
                      ยังไม่มีรายการ — กดปุ่มเพิ่มเสา หรือเพิ่มค่าใช้จ่าย
                    </td>
                  </tr>
                ) : (
                  lines.map((line, index) => (
                    <JobClosingLineRow
                      key={line.clientId}
                      line={line}
                      index={index}
                      disabled={isSubmitting}
                      onChange={updated => updateLine(line.clientId, updated)}
                      onRemove={() => removeLine(line.clientId)}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className='modal-footer'>
          <button
            type='button'
            className='btn btn-light fw-bold'
            disabled={isSubmitting}
            onClick={onClose}>
            ยกเลิก
          </button>
          <button
            type='button'
            className='btn btn-secondary fw-bold'
            disabled={isSubmitting}
            onClick={() => handleSave(false)}>
            {isSubmitting ? (
              <span className='spinner-border spinner-border-sm' />
            ) : (
              'บันทึกร่าง'
            )}
          </button>
          <button
            type='button'
            className='btn btn-primary fw-bold'
            disabled={isSubmitting}
            onClick={() => handleSave(true)}>
            {isSubmitting ? (
              <>
                <span className='spinner-border spinner-border-sm me-2' />
                กำลังบันทึก...
              </>
            ) : (
              'บันทึกและออกเอกสาร'
            )}
          </button>
        </div>
      </div>
    </Modal>,
    modalsRoot
  )
}

export default JobClosingForm
